import { getModalState } from '../app-state/modals'
import { setScreenOrientation } from '../app-state/state.screen'
import {
    SIDEBAR_BREAKPOINT,
    SIDEBAR_MINUMUM_BREAKPOINT,
} from '../components/styles/breakpoints'
import { motionTransition } from '../utilities/transitions'
import { pubSubSidebar } from './pubSub'
import { APPSTATE } from './state.app'
import { resetPortalDimensions } from './state.layout'

export enum SidebarEvent {
    SETTING = 'setting',
    STATE = 'state',
}
export enum SidebarType {
    Friends = 'friends',
    Participants = 'participants',
}

export type SidebarState = {
    show?: boolean
    type?: SidebarType
    options?: { id?: number }
}
export type Sidebar = {
    setting?: SidebarState
    state?: SidebarState
}

export const SIDEBAR: Sidebar = {
    setting: {
        show: undefined,
        type: SidebarType.Friends,
    },
    state: {
        show: undefined,
        type: SidebarType.Friends,
    },
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_SIDEBAR__ = SIDEBAR
}

/**
 * sets the current sidebar state. does not change user-set setting.
 */
const setSidebarState = (state: SidebarState) => {
    const { show: previousShow, type: previousType } = SIDEBAR.state
    const nextState: SidebarState = {
        ...SIDEBAR.state,
        ...state,
    }
    SIDEBAR.state = nextState
    const showChanged = !!nextState.show !== !!previousShow
    const shouldUpdate = showChanged || nextState.type !== previousType
    if (!shouldUpdate) return
    if (getModalState().floating_room.show) {
        motionTransition(() => {
            pubSubSidebar.emit(SidebarEvent.STATE, nextState, true)
            // portal width will have changed, so clear cached width
            resetPortalDimensions(true)
            const sizeChanged = showChanged || !APPSTATE.app.authenticated // if we aren't authed we leverage it for participants only
            // console.log('[setSidebarState]', { sizeChanged, showChanged })
            if (sizeChanged) setScreenOrientation({ overrideSkip: true }) //updateFullscreenSize()
        })
        return
    }
    pubSubSidebar.emit(SidebarEvent.STATE, nextState, true)
}

/** sets the user settings: `show`, `type` */
const setSidebar = (setting: SidebarState) => {
    const nextSetting: SidebarState = {
        ...SIDEBAR.setting,
        ...setting,
    }
    SIDEBAR.setting = nextSetting
    setSidebarState(setting)
}

/**
 * used to display sidebar if window width > 1280.
 * does not change user-set settings.
 */
export const setSidebarVisibilityState = (visible: boolean) => {
    setSidebarState({ show: visible })
}

/**
 * used to switch sidebar type on navigation (switch to friends when exiting room).
 * does not change user-set settings.
 */
const setSidebarTypeState = (type: SidebarType) => {
    setSidebarState({ type })
}

/** changes user setting `show` */
export const toggleSidebarVisibility = (force?: boolean) => {
    if (typeof force === 'boolean') {
        setSidebar({ show: force })
        return
    }
    const current = !!SIDEBAR.state.show
    setSidebar({ show: !current })
}

/** changes user setting `type` */
export const toggleSidebarType = () => {
    const nextType =
        SIDEBAR.state.type === SidebarType.Friends
            ? SidebarType.Participants
            : SidebarType.Friends
    // console.log('toggleSidebarType', nextType)
    setSidebar({ type: nextType })
}

export const onRouteChangeSidebarType = (url: string) => {
    const isRoomPage = url.startsWith('/room')
    const nextType =
        SIDEBAR.setting.type === SidebarType.Participants && isRoomPage
            ? SidebarType.Participants
            : SidebarType.Friends
    if (SIDEBAR.state.type !== nextType) {
        setSidebarTypeState(nextType)
    }
}

/** update initial visibility state */
export const initializeSidebarState = (windowWidth: number) => {
    setSidebarVisibilityState(
        SIDEBAR.setting.show
            ? windowWidth >= SIDEBAR_MINUMUM_BREAKPOINT
            : (SIDEBAR.setting.show ?? windowWidth > SIDEBAR_BREAKPOINT),
    )
}
